import { GQLQueryOrderInput } from "@rx-cs-system/schema";
import { useCallback } from "react";
import styled from "styled-components";

import { PageBody } from "../PageBody";
import {
  DataListBody,
  DataListBodyItem,
  DataListHeader,
  DataListHeaderItem,
  DataListRow,
  DataTable,
  SwitchHeaderOrder,
} from "../../atoms/Table";
import { SearchInput } from "../../atoms/SearchInput";
import { Typography } from "../../atoms/Typography";
import { LoadMoreButton } from "../../atoms/LoadMoreButton";

/**
 * 処方一覧ページ
 */

type ColumnName =
  | "id"
  | "name"
  | "appName"
  | "prescriptionCode"
  | "prescribedAt";

export type PatientsScProps = {
  patients: PatientSc[];
  searchValue: string;
  order?: GQLQueryOrderInput;
  onSearchClick: (keyword: string) => void;
  onResetClick: () => void;
  onSortClick: (input: GQLQueryOrderInput) => void;
  onRowClick: (id: string) => void;
  onLoadMoreClick: (skip: number) => void;
};

type PatientSc = {
  id: string;
  name: string;
  appName: string;
  appImage: string;
  // pdtId: string;
  // appUrl: string;
  prescriptionCode?: string | null;
  prescribedAt?: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 40px;
  height: 100%;
`;

const NameWrapper = styled.div`
  max-width: 100%;
`;

const AppWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 204px;
  img {
    flex-shrink: 0;
    margin-right: 8px;
  }
`;

export function PatientScPage({
  patients,
  searchValue,
  order,
  onSearchClick,
  onResetClick,
  onSortClick,
  onRowClick,
  onLoadMoreClick,
}: PatientsScProps) {
  const sortClick = useCallback(
    (value: ColumnName) => {
      onSortClick(SwitchHeaderOrder(value, order));
    },
    [onSortClick, order],
  );

  const header = (
    <>
      <DataListHeaderItem
        onSort={() => sortClick("id")}
        order={order?.field === "id" ? order.value : undefined}
        align="center"
      >
        <Typography size={1} bold>
          ID
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("name")}
        order={order?.field === "name" ? order.value : undefined}
      >
        <Typography size={1} bold>
          氏名
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("appName")}
        order={order?.field === "appName" ? order.value : undefined}
      >
        <Typography size={1} bold>
          アプリ名
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("prescriptionCode")}
        order={order?.field === "prescriptionCode" ? order.value : undefined}
      >
        <Typography size={1} bold>
          処方コード
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("prescribedAt")}
        order={order?.field === "prescribedAt" ? order.value : undefined}
      >
        <Typography size={1} bold>
          処方日
        </Typography>
      </DataListHeaderItem>
    </>
  );

  const searchInput = (
    <SearchInput
      value={searchValue}
      placeholder={"キーワードを入力"}
      onSearch={onSearchClick}
      onReset={onResetClick}
    />
  );

  return (
    <PageBody title="処方一覧" searchInput={searchInput}>
      <Container>
        <DataTable>
          <DataListHeader row={header} flexRatio={[2, 3, 3, 2, 1]} />
          <DataListBody
            rows={patients.map((patient: PatientSc) => (
              <DataListRow
                flexRatio={[2, 3, 3, 2, 1]}
                key={patient.id}
                onRowClick={() => onRowClick(patient.id)}
                // onMouseOver={() => rowHover(patient.id)}
                // onMouseLeave={rowLeave}
              >
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {patient.id}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <NameWrapper>
                    <Typography size={2} withEllipsis={true}>
                      {patient.name}
                    </Typography>
                  </NameWrapper>
                </DataListBodyItem>
                <DataListBodyItem>
                  <AppWrapper>
                    <img
                      src={patient.appImage}
                      width="36"
                      height="36"
                      alt={"app_logo"}
                    />
                    <Typography size={2} withEllipsis={true}>
                      {patient.appName}
                    </Typography>
                  </AppWrapper>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {patient.prescriptionCode}
                  </Typography>
                </DataListBodyItem>
                <DataListBodyItem>
                  <Typography size={2} withEllipsis={true}>
                    {patient.prescribedAt}
                  </Typography>
                </DataListBodyItem>
              </DataListRow>
            ))}
          />
        </DataTable>
        <LoadMoreButton onClick={() => onLoadMoreClick(patients.length)} />
      </Container>
    </PageBody>
  );
}
